import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Bottom sheet за телефон: панелът („Нови маси" / редакция) изплува отдолу
 * върху мрежата. Затваря се от фона, от „Затвори" в панела и с Esc (Esc се
 * обработва от страницата — с preventDefault). `dk-elevated` — плаваща
 * повърхност в тъмната тема.
 */
export const TablesBottomSheet = ({ children, onClose }: { children: ReactNode; onClose: () => void }) => (
  <>
    <div aria-hidden onClick={onClose} className="fixed inset-0 z-40 bg-[rgba(17,17,17,0.4)]" />
    <div
      role="dialog"
      aria-modal="true"
      className="dk-elevated fixed inset-x-0 bottom-0 z-40 max-h-[88dvh] overflow-y-auto overscroll-contain rounded-t-[20px] bg-surface px-4 pb-[calc(20px+env(safe-area-inset-bottom,0px)*0.5)] pt-2 shadow-[0_-12px_32px_rgba(17,17,17,0.18)]"
    >
      <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-shade-4" />
      {children}
    </div>
  </>
);

/**
 * Лентата долу на телефон при избрани маси (докато sheet-ът е затворен):
 * броят + „Откажи" + „Редактирай" (отваря панела като bottom sheet). Така
 * може да се избират още плочки, без панелът да ги закрива.
 */
export const SelectionBar = ({
  count,
  onEdit,
  onClear,
}: {
  count: number;
  onEdit: () => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="dk-elevated fixed inset-x-3 bottom-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)] z-30 flex items-center gap-2 rounded-[16px] border border-shade-2 bg-surface p-2 pl-4 shadow-[0_10px_30px_rgba(17,17,17,0.18)]">
      <span className="min-w-0 flex-1 truncate text-[14px] font-bold leading-[1.3] tabular-nums text-ink">
        {t("tablesConfig.bulk.title", { count })}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="h-11 shrink-0 cursor-pointer rounded-[10px] border border-shade-3 bg-surface px-3.5 text-[13.5px] font-semibold text-ink transition-colors duration-150 hover:bg-fill"
      >
        {t("common.cancel")}
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="h-11 shrink-0 cursor-pointer rounded-[10px] border-none bg-brand px-4 text-[13.5px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep"
      >
        {t("tablesConfig.bulk.edit")}
      </button>
    </div>
  );
};
